import { useCallback, useEffect, useState } from "react";
import { useWatch } from "react-hook-form";
import { useFetch } from "@hooks/useFetch";
import { IAddress, IUserAddress } from "@interface/customerDetails/IAddress";
import { getEircodeFinderRoute } from "@api-routes/addressFinder";
import { eircodeRegex } from "@utils/regex";
import { removeWhitespace } from "@utils/stringUtils";
import { AutocompleteInputChangeReason } from "@mui/material";
import { addressFormatter } from "@utils/addressFormatter";
import { useTranslation } from "react-i18next";
import type { ControllerRenderProps, FieldErrors, FieldValues, UseFormMethods } from "react-hook-form";

interface IParams<TFieldValues extends FieldValues> {
    addressForm: UseFormMethods<TFieldValues> & Partial<UseFormMethods>;
    prefix?: string;
    getSelectedAddress?(address: IAddress): void;
}

export const useAutoCompleteEirCode = <TFieldValues extends FieldValues>(props: IParams<TFieldValues>) => {
    const { addressForm, prefix, getSelectedAddress } = props;
    const { t } = useTranslation(["address", "common"]);

    const formErrors = (prefix ? addressForm.errors[prefix] : addressForm.errors) as FieldErrors<IUserAddress>;
    const namePrefix = prefix ? `${prefix}.` : "";

    const [getEircodeAddressListState, { get }] = useFetch();
    const [eircodeAddressList, setEircodeAddressList] = useState<IAddress[]>();

    const eirCodeValue = useWatch<string>({
        name: `${namePrefix}code`,
        control: addressForm.control,
        defaultValue: "",
    });

    const resetFields = useCallback(() => {
        addressForm.setValue(`${namePrefix}addressLine1`, "");
        addressForm.setValue(`${namePrefix}addressLine2`, "");
        addressForm.setValue(`${namePrefix}addressLine3`, "");
        addressForm.setValue(`${namePrefix}town`, "");
        addressForm.setValue(`${namePrefix}county`, "");

        setEircodeAddressList(undefined);
    }, [addressForm, namePrefix]);

    const submitEircode = useCallback(
        (eirCode: string) => {
            setEircodeAddressList(undefined);

            if (eirCode) {
                const eirCodeFormatted = removeWhitespace(eirCode) as string;
                get<{ addresses: IAddress[] }>(getEircodeFinderRoute(eirCodeFormatted), { isSecured: false }).then(
                    (response) => {
                        if (response) {
                            setEircodeAddressList(response.addresses ?? []);
                        }
                    }
                );
            }
        },
        [get]
    );

    const fillAddress = useCallback(
        (newAddress: IAddress) => {
            addressForm.setValue(`${namePrefix}addressLine1`, newAddress.addressLine1);
            addressForm.setValue(`${namePrefix}addressLine2`, newAddress.addressLine2 ?? "");
            addressForm.setValue(`${namePrefix}addressLine3`, newAddress.addressLine3 ?? "");
            addressForm.setValue(`${namePrefix}town`, newAddress.town);
            addressForm.setValue(`${namePrefix}county`, newAddress.county);
        },
        [addressForm, namePrefix]
    );

    const selectAddress = useCallback(
        (address: IAddress) => {
            fillAddress(address);

            if (getSelectedAddress) {
                getSelectedAddress(address);
            }
        },
        [fillAddress, getSelectedAddress]
    );

    const handleChange = useCallback(
        (newValue: IAddress | string) => {
            if (newValue && typeof newValue !== "string") {
                selectAddress(newValue);
            }
        },
        [selectAddress]
    );

    const handleInputChange = useCallback(
        (
            controllerRenderProps: ControllerRenderProps,
            newInputValue: string,
            reason: AutocompleteInputChangeReason
        ) => {
            if (reason === "input") {
                controllerRenderProps.onChange(newInputValue);
            }
        },
        []
    );

    const getDefaultMessage = useCallback(() => {
        let message = t("address:noAddressFound");

        if (getEircodeAddressListState.isLoading) {
            message = `${t("common:loading")}...`;
        } else if (getEircodeAddressListState.error) {
            message = getEircodeAddressListState.error.errorMessage || t("common:genericError");
        }

        return message;
    }, [getEircodeAddressListState, t]);

    const getOptionLabel = useCallback(
        (option: IAddress | string) => (typeof option === "string" ? option : addressFormatter(option)),
        []
    );

    const getFilterOptions = useCallback((options: IAddress[]) => options, []);

    useEffect(() => {
        if (!eircodeRegex.test(eirCodeValue)) {
            resetFields();

            return;
        }

        submitEircode(eirCodeValue);
    }, [eirCodeValue, resetFields, submitEircode]);

    return {
        formErrors,
        namePrefix,
        handleChange,
        handleInputChange,
        getDefaultMessage,
        getOptionLabel,
        getFilterOptions,
        eircodeAddressList,
        getEircodeAddressListState,
    };
};
